import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog.tsx";
import { type ReactElement, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { userTaskQueries } from "@/components/userTasks/api.ts";
import { ThreadUserTaskList } from "@/components/userTasks/ThreadUserTasks.tsx";
import { useTaskGates } from "@/components/userTasks/useTaskGates.ts";

import { TASK_DIALOG_CONTENT_CLASS, TaskList } from "./TaskList.tsx";
import { taskQueries } from "./useTasks.ts";

/**
 * This thread's tasks — what the assistant took on here and what came of it.
 *
 * Every status, deliberately: a task that was finished or dropped is as much
 * part of the record as one still open. Updates live while the thread runs.
 *
 * With `tasks-v2` the thread's tasks are on the user's list, and are read and
 * shown from there; the per-thread table serves everyone else.
 */
export const ThreadTasksDialog = ({
  threadId,
  open,
  onOpenChange,
  trigger,
  onCloseAutoFocus,
}: {
  threadId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * The control rendered as the dialog's Radix trigger, so focus returns to it
   * when the dialog closes.
   */
  trigger?: ReactElement;
  /** Restores focus when the opener was an ephemeral menu item. */
  onCloseAutoFocus?: (event: Event) => void;
}) => {
  const { list: onList } = useTaskGates();
  const activeThreadId = open ? threadId : undefined;
  const perThread = useQuery(taskQueries.threadTasks(onList ? undefined : activeThreadId));
  const listed = useQuery(userTaskQueries.thread(onList ? activeThreadId : undefined));
  // Stable, so the memoized list does not re-render on every dialog render.
  const { refetch: refetchListed } = listed;
  const retryListed = useCallback(() => void refetchListed(), [refetchListed]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent
        className={TASK_DIALOG_CONTENT_CLASS}
        onCloseAutoFocus={onCloseAutoFocus}
        // Escape closes this dialog and stops there. The screen-level
        // Escape-to-back shortcut stands down for an open dialog by looking for
        // one in the DOM, but Radix has already unmounted this one by the time
        // that check runs, so the user would land back on the thread list
        // instead of on the thread they were reading. `stopImmediatePropagation`
        // rather than `stopPropagation`: both handlers sit on `document`, and
        // only the immediate form stops a sibling listener on the same node.
        onEscapeKeyDown={(event) => event.stopImmediatePropagation()}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>Tasks</DialogTitle>
          <DialogDescription className="sr-only">
            Tasks Okthx is tracking for this thread
          </DialogDescription>
        </DialogHeader>
        {/* `min-h-0` is load-bearing: a flex child's default `min-height:auto`
            refuses to shrink below its content, so without it this never
            scrolls and the cap just clips. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
          {onList ? (
            <ThreadUserTaskList
              tasks={listed.data?.tasks ?? []}
              isLoading={listed.isLoading}
              isError={listed.isError}
              onRetry={retryListed}
              emptyMessage="No tasks on this thread yet."
            />
          ) : (
            <TaskList
              tasks={perThread.data?.tasks ?? []}
              isLoading={perThread.isLoading}
              isError={perThread.isError}
              onRetry={() => void perThread.refetch()}
              showThread={false}
              emptyMessage="No tasks on this thread yet."
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
